import React, { useCallback, useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Bell, BellOff, Smartphone } from 'lucide-react';
import {
  pushSupported, getPushPermission, isPushOffOnDevice, turnOffPushOnDevice, turnOnPushOnDevice,
} from '../native/push';
import { useAuth, isViewingAs, VIEWING_AS_NOTE } from '../context/AuthContext';

/**
 * NotificationsToggle — My account → Notifications (B-88).
 *
 *  1. THIS DEVICE (app only): a switch. Off drops this phone's registration
 *     and remembers the choice for this person, so the app doesn't register
 *     it again on the next start (native/push.js). On registers it again
 *     (asking the OS first if it never has). If the OS blocks Marakin we
 *     can't re-prompt, so we point at the phone's Settings. On the website
 *     push comes through the app, so the "App only" card stays.
 *  2. WHAT YOU GET (account-wide, every phone): one switch per kind, saved
 *     with PUT /api/me/notification-preferences; the server drops switched-off
 *     kinds for every device (lib/push.js). Defaults all on. Support replies,
 *     answers to your own requests and notices about your children always go.
 */

const KINDS = [
  { key: 'fixtures', label: 'Fixtures & training',
    detail: "New fixtures, training sessions and events, and reminders to say if you're going. Cancellations always come through." },
  { key: 'news', label: 'Club news', detail: 'New posts from your club.' },
  { key: 'kit_swap', label: 'Kit Swap',
    detail: 'Messages about an item, and when one of yours is claimed or a claim of yours ends.' },
  // B-90: the same switch covers "Confirm Sam S. for U11s?" (team managers + admins).
  { key: 'join_requests', label: 'Join and team requests',
    detail: 'When someone asks to join a club you run, or a parent adds their child to a team you manage.', adminsOnly: true },
];

function Switch({ checked, onChange, disabled, labelledBy, describedBy }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelledBy}
      aria-describedby={describedBy}
      className={`notif-switch${checked ? ' is-on' : ''}`}
      disabled={disabled}
      onClick={() => onChange(!checked)}
    >
      <span className="notif-switch-knob" aria-hidden="true" />
    </button>
  );
}

function DeviceSwitch({ userId, viewingAs }) {
  // 'loading' | 'on' | 'off' | 'blocked'
  const [state, setState] = useState('loading');
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');

  const refresh = useCallback(async () => {
    const perm = await getPushPermission();
    if (perm === 'denied') setState('blocked');
    else setState(perm === 'granted' && !isPushOffOnDevice(userId) ? 'on' : 'off');
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);
  // Back from the phone's Settings: the OS permission may have changed.
  useEffect(() => {
    const onVisible = () => { if (document.visibilityState === 'visible') refresh(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refresh]);

  const change = async (next) => {
    setBusy(true);
    setProblem('');
    const r = next ? await turnOnPushOnDevice(userId) : await turnOffPushOnDevice(userId);
    if (!next && !r.ok) setProblem("Couldn't switch off — check your connection and try again.");
    if (next && !r.ok && r.reason === 'error') setProblem("Couldn't switch on — try again in a moment.");
    await refresh();
    setBusy(false);
  };

  const on = state === 'on';
  return (
    <div className="account-card">
      <div className="notif-row notif-row-first">
        <div className="notif-text">
          <span className="notif-label" id="notif-device-label">
            {on ? <Bell size={15} aria-hidden="true" /> : <BellOff size={15} aria-hidden="true" />}
            {' '}This device
          </span>
          <span className="notif-detail" id="notif-device-detail">
            {state === 'blocked' ? 'Blocked in your phone settings'
              : on ? 'Notifications are on for this phone.'
              : state === 'loading' ? 'Checking…'
              : 'Notifications are off for this phone.'}
          </span>
        </div>
        {state !== 'blocked' && (
          <Switch
            checked={on}
            onChange={change}
            disabled={busy || viewingAs || state === 'loading'}
            labelledBy="notif-device-label"
            describedBy="notif-device-detail"
          />
        )}
      </div>
      {state === 'blocked' && (
        <p className="section-help notif-foot">
          Notifications are blocked for Marakin. Turn them back on in your phone's
          Settings → Marakin → Notifications.
        </p>
      )}
      {problem && <p className="notif-problem" role="alert">{problem}</p>}
    </div>
  );
}

export default function NotificationsToggle() {
  const native = pushSupported();
  const qc = useQueryClient();
  const { user, impersonating } = useAuth();
  // B-65: personal settings are the person's own (the server refuses a
  // staff "view as" session, and that session never registers a device).
  const viewingAs = isViewingAs(impersonating);
  const [problem, setProblem] = useState('');
  const runsAClub = (user?.memberships || []).some(m => !m.implicit
    && (m.role === 'admin' || (m.teamRoles || []).some(tr => tr.role === 'team_manager')));

  // Per-user key: never show one person's switches after an account switch.
  const key = ['me-notification-prefs', user?.id];
  const { data, isLoading, isError } = useQuery({
    queryKey: key,
    queryFn: () => axios.get('/me/notification-preferences').then(r => r.data),
    enabled: !!user?.id,
  });

  const save = useMutation({
    mutationFn: (change) => axios.put('/me/notification-preferences', { push: change }).then(r => r.data),
    onMutate: async (change) => {
      setProblem('');
      await qc.cancelQueries({ queryKey: key });
      const before = qc.getQueryData(key);
      qc.setQueryData(key, old => ({ ...old, push: { ...(old?.push || {}), ...change } }));
      return { before };
    },
    onError: (err, change, ctx) => {
      qc.setQueryData(key, ctx?.before);
      setProblem(err.response?.data?.code === 'impersonating'
        ? VIEWING_AS_NOTE
        : "Couldn't save — check your connection and try again.");
    },
    onSuccess: (fresh) => qc.setQueryData(key, fresh),
  });

  const push = data?.push || {};
  const kinds = KINDS.filter(k => !k.adminsOnly || runsAClub || push[k.key] === false);

  return (
    <>
      {native ? (
        <DeviceSwitch userId={user?.id} viewingAs={viewingAs} />
      ) : (
        <div className="account-card">
          <div className="profile-row">
            <span className="profile-label"><Smartphone size={15} style={{ verticalAlign: '-2px' }} /> Push notifications</span>
            <span className="profile-value"><em className="muted">App only</em></span>
          </div>
          <p className="section-help" style={{ marginTop: 8, marginBottom: 0 }}>
            Get instant alerts for fixtures, cancellations and replies by installing
            the Marakin app from the App Store or Google Play, then sign in here.
          </p>
        </div>
      )}

      <div className="account-card" style={{ marginTop: 12 }}>
        <p className="notif-heading">What you get</p>
        <p className="section-help" style={{ marginBottom: 6 }}>
          These apply on every phone you're signed in on.
        </p>
        {isLoading ? (
          <p className="muted" style={{ fontSize: 14, margin: 0 }}>Loading…</p>
        ) : isError ? (
          <p className="notif-problem" role="alert">Couldn't load your notification settings. Try again later.</p>
        ) : (
          kinds.map(k => (
            <div className="notif-row" key={k.key}>
              <div className="notif-text">
                <span className="notif-label" id={`notif-${k.key}-label`}>{k.label}</span>
                <span className="notif-detail" id={`notif-${k.key}-detail`}>{k.detail}</span>
              </div>
              <Switch
                checked={push[k.key] !== false}
                onChange={(next) => save.mutate({ [k.key]: next })}
                disabled={viewingAs || save.isPending}
                labelledBy={`notif-${k.key}-label`}
                describedBy={`notif-${k.key}-detail`}
              />
            </div>
          ))
        )}
        {problem && <p className="notif-problem" role="alert">{problem}</p>}
        <p className="section-help notif-foot">
          Cancellations, replies from Marakin support, answers to your own requests
          and anything about access to your children always come through.
        </p>
        {viewingAs && <p className="muted" style={{ fontSize: 13, margin: '8px 0 0' }}>{VIEWING_AS_NOTE}</p>}
      </div>
    </>
  );
}
